import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { haversineDistanceKm } from '../lib/geoUtils';
import { getAuthedSupabaseUser } from './profileApi';
import { catalogRowForPick, REGION_CITY_ROWS } from '../data/regionsCatalog';
import { getCityNativeLabel } from '../data/onboardingRegionLabels';

/** catalog key → city_master.city_code (post-20260913: matches locations.code) */
const CATALOG_KEY_TO_CITY_CODE = {
  danang: 'danang',
  hochiminh: 'hochiminh',
  hoian: 'hoian',
  nhatrang: 'nhatrang',
  dalat: 'dalat',
  phuquoc: 'phuquoc',
  vungtau: 'vungtau',
  haiphong: 'haiphong',
  cantho: 'cantho',
};

function catalogKeyToCityCode(key) {
  const k = String(key || '').toLowerCase();
  return CATALOG_KEY_TO_CITY_CODE[k] || k;
}

function findCatalogRowForLabel(label) {
  const raw = normCityLabel(label);
  if (!raw) return null;
  return (
    catalogRowForPick(label) ||
    REGION_CITY_ROWS.find((r) => {
      const names = [r.pick, r.ko, getCityNativeLabel(r)].map(normCityLabel);
      return names.includes(raw);
    }) ||
    null
  );
}

function normCityLabel(s) {
  return String(s || '')
    .trim()
    .normalize('NFC')
    .toLowerCase()
    .replace(/\(gps\)/gi, '')
    .replace(/\s+/g, ' ');
}

/** user_location_settings.user_id FK + RLS use auth.users.id (auth.uid()), not public.users.id */
async function resolveAuthUserIdForLocationSettings() {
  const sb = getSupabase();
  if (!sb) return null;
  const user = await getAuthedSupabaseUser(sb);
  return user?.id ?? null;
}

// 전체 지역 조회
export const fetchLocations = async ({
  level = null,
  parentCode = null,
  countryCode = null,
} = {}) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  let q = sb
    .from('locations')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');
  if (level) q = q.eq('level', level);
  if (countryCode) q = q.eq('country_code', countryCode);
  if (parentCode) {
    const { data: parent } = await sb
      .from('locations')
      .select('id')
      .eq('code', parentCode)
      .maybeSingle();
    if (parent) q = q.eq('parent_id', parent.id);
  }
  const { data } = await q;
  return data ?? [];
};

export const fetchCountries = () => fetchLocations({ level: 'country' });

export const fetchCities = (countryCode) => fetchLocations({ level: 'city', countryCode });

export const fetchDistricts = (cityCode) => fetchLocations({ level: 'district', parentCode: cityCode });

/**
 * UI 라벨(다낭/Đà Nẵng/Da Nang) → locations.id
 * @param {string} cityLabel
 * @param {{ countryCode?: string, language?: string, locationRow?: object|null, coords?: { lat: number, lng: number }|null, fallbackLabel?: string }} [opts]
 */
export async function resolveCityLocationId(cityLabel, opts = {}) {
  if (!isSupabaseConfigured) return null;

  const locationRow = opts.locationRow || null;
  if (locationRow?.id && String(locationRow.level || '').toLowerCase() === 'city') {
    return locationRow.id;
  }

  const coords = opts.coords || null;
  if (coords?.lat != null && coords?.lng != null) {
    const nearest = await findNearestLocation(coords.lat, coords.lng, 'city');
    if (nearest?.id) return nearest.id;
  }

  const countryCode = opts.countryCode || locationRow?.country_code || 'VN';
  const language = opts.language || 'ko';
  const stripCountryPrefix = (s) => {
    const raw = String(s || '').trim();
    if (!raw.includes('·')) return raw;
    return raw.split('·').pop()?.trim() || raw;
  };
  const candidates = [cityLabel, opts.fallbackLabel, locationRow ? getLocationName(locationRow, language) : null]
    .map((v) => stripCountryPrefix(v))
    .map((v) => String(v || '').trim())
    .filter(Boolean);

  let catalogForCountry = null;
  for (const label of candidates) {
    const row = findCatalogRowForLabel(label);
    if (row) {
      catalogForCountry = row;
      break;
    }
  }

  const fetchCountry = catalogForCountry?.country || countryCode;
  // 호이안/냐짱/붕따우/푸꾸옥/달랏처럼 UI에서는 "도시"급으로 다루지만 locations 테이블에는
  // level='district'로 들어가 있는 주요 지명이 있음. level='city'만 조회하면 이런 지명을
  // 영영 못 찾아 관심 지역(수동 선택) 저장이 항상 실패했던 버그 — district도 함께 후보에 포함.
  const [cities, prominentDistricts] = await Promise.all([
    fetchCities(fetchCountry),
    fetchLocations({ level: 'district', countryCode: fetchCountry }),
  ]);
  const pickableLocations = [...cities, ...prominentDistricts];
  if (!pickableLocations.length) return null;

  for (const label of candidates) {
    const raw = normCityLabel(label);
    if (!raw) continue;

    const catalog = findCatalogRowForLabel(label);
    const catalogCode = catalog?.key ? catalogKeyToCityCode(catalog.key) : '';

    for (const loc of pickableLocations) {
      const locCode = String(loc.code || '').toLowerCase();
      if (catalogCode && locCode === String(catalogCode).toLowerCase()) {
        return loc.id;
      }
      if (catalog?.key && locCode === String(catalog.key).toLowerCase()) {
        return loc.id;
      }
      const locNames = [loc.name_en, loc.name_ko, loc.name_local, getLocationName(loc, 'ko'), getLocationName(loc, 'vi'), getLocationName(loc, 'en')]
        .map(normCityLabel)
        .filter(Boolean);
      if (locNames.includes(raw)) return loc.id;
      const en = normCityLabel(loc.name_en);
      if (en && (raw === en || raw.startsWith(`${en} `) || en.startsWith(`${raw} `))) return loc.id;
    }
  }

  return null;
}

/**
 * UI 라벨 → city_master.id (관심지역 반경 저장용)
 * @param {string} cityLabel
 * @param {{ countryCode?: string, cityEn?: string, fallbackLabel?: string }} [opts]
 */
export async function resolveCityMasterId(cityLabel, opts = {}) {
  if (!isSupabaseConfigured) return null;
  const sb = getSupabase();
  if (!sb) return null;

  const countryCode = String(opts.countryCode || 'VN').toUpperCase();
  const candidates = [cityLabel, opts.fallbackLabel, opts.cityEn]
    .map((v) => String(v || '').trim())
    .filter(Boolean);

  for (const label of candidates) {
    const catalog = findCatalogRowForLabel(label);
    if (catalog) {
      const cityCode = catalogKeyToCityCode(catalog.key);
      const { data } = await sb
        .from('city_master')
        .select('id')
        .eq('country_code', catalog.country || countryCode)
        .eq('city_code', cityCode)
        .maybeSingle();
      if (data?.id) return data.id;
    }
  }

  for (const label of candidates) {
    const raw = normCityLabel(label);
    if (!raw) continue;
    const { data } = await sb
      .from('city_master')
      .select('id')
      .eq('country_code', countryCode)
      .or(`name_en.ilike.%${raw}%,name_ko.ilike.%${raw}%`)
      .limit(1)
      .maybeSingle();
    if (data?.id) return data.id;
  }

  return null;
}

// GPS로 가장 가까운 위치 찾기 (Haversine km; maxDistanceKm 초과 시 null = 매핑 거부)
export const findNearestLocation = async (lat, lng, level = 'city', maxDistanceKm = 50) => {
  if (!isSupabaseConfigured) return null;
  const sb = getSupabase();
  if (!sb) return null;
  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;
  const maxKm = Number(maxDistanceKm);
  const capKm = Number.isFinite(maxKm) && maxKm > 0 ? maxKm : 50;

  const { data } = await sb
    .from('locations')
    .select('*')
    .eq('level', level)
    .eq('is_active', true);
  if (!data?.length) return null;

  let nearest = null;
  let minDistKm = Infinity;
  data.forEach((loc) => {
    if (loc.lat == null || loc.lng == null) return;
    const distKm = haversineDistanceKm(latN, lngN, Number(loc.lat), Number(loc.lng));
    if (distKm < minDistKm) {
      minDistKm = distKm;
      nearest = loc;
    }
  });
  if (!nearest || minDistKm > capKm) return null;
  return nearest;
};

// 유저 언어에 맞는 지역명 (locations 테이블 — GPS 근접 도시)
export const getLocationName = (location, language = 'ko') => {
  if (!location) return '';
  if (language === 'ko') {
    return location.name_ko || location.name_en || location.name_local || '';
  }
  if (language === 'vi') {
    return location.name_vi || location.name_local || location.name_en || location.name_ko || '';
  }
  if (language === 'ru') {
    return location.name_ru || location.name_en || location.name_ko || location.name_local || '';
  }
  if (language === 'zh') {
    return location.name_zh || location.name_en || location.name_ko || location.name_local || '';
  }
  if (language === 'ja') {
    return location.name_ja || location.name_en || location.name_ko || location.name_local || '';
  }
  return location.name_en || location.name_ko || location.name_local || '';
};

// 유저 위치 설정 조회 (userId 인자는 하위호환용 — 실제 조회는 auth.uid())
export const fetchUserLocationSettings = async (_userId) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const authUserId = await resolveAuthUserIdForLocationSettings();
  if (!authUserId) return [];
  const { data, error } = await sb
    .from('user_location_settings')
    .select(
      `
        *,
        locations(*)
      `,
    )
    .eq('user_id', authUserId)
    .order('is_primary', { ascending: false });
  if (error) {
    console.warn('[locationApi] fetchUserLocationSettings', error.message);
    return [];
  }
  return data ?? [];
};

// 유저 위치 설정 저장/수정
export const upsertUserLocationSetting = async ({
  userId: _userId,
  locationId,
  cityMasterId: _legacyCityMasterId,
  radiusKm = 10,
  isPrimary = false,
  label = null,
  isUserSaved,
}) => {
  const resolvedLocationId = locationId || _legacyCityMasterId;
  if (!resolvedLocationId) return null;
  if (!isSupabaseConfigured) return null;
  const sb = getSupabase();
  if (!sb) return null;
  const authUserId = await resolveAuthUserIdForLocationSettings();
  if (!authUserId) throw new Error('NOT_AUTHENTICATED');
  const { data, error } = await sb
    .from('user_location_settings')
    .upsert(
      {
        user_id: authUserId,
        location_id: resolvedLocationId,
        radius_km: radiusKm,
        is_primary: isPrimary,
        label,
        updated_at: new Date().toISOString(),
        // 넘어온 경우에만 포함 — 안 넘기면 기존 값(또는 신규행 기본값 false) 유지
        ...(isUserSaved !== undefined ? { is_user_saved: isUserSaved } : {}),
      },
      {
        onConflict: 'user_id,location_id',
      },
    )
    .select()
    .maybeSingle();
  if (error) {
    console.warn('[locationApi] upsertUserLocationSetting', error.message, {
      locationId: resolvedLocationId,
      radiusKm,
      isPrimary,
      code: error.code,
    });
    throw error;
  }
  return data;
};

export const deleteUserLocationSetting = async (id) => {
  if (!isSupabaseConfigured || !id) return false;
  const sb = getSupabase();
  if (!sb) return false;
  const authUserId = await resolveAuthUserIdForLocationSettings();
  if (!authUserId) return false;
  const { error } = await sb
    .from('user_location_settings')
    .delete()
    .eq('id', id)
    .eq('user_id', authUserId);
  if (error) {
    console.warn('[locationApi] deleteUserLocationSetting', error.message);
    return false;
  }
  return true;
};

// 반경 옵션
export const RADIUS_OPTIONS = [
  { km: 1, label: '1km', desc: '도보 거리' },
  { km: 3, label: '3km', desc: '자전거 거리' },
  { km: 5, label: '5km', desc: '가까운 동네' },
  { km: 10, label: '10km', desc: '기본 범위' },
  { km: 20, label: '20km', desc: '차로 20분' },
  { km: 50, label: '50km', desc: '인근 도시 포함' },
  { km: 100, label: '100km', desc: '광역 범위' },
];
